import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Copy, DollarSign, MousePointerClick, TrendingUp, Link2, LogOut, Loader2, Clock, CreditCard, Banknote } from 'lucide-react';

const TOKEN_KEY = 'sparky_affiliate_token';

function AffiliateLogin({ onLogin }) {
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [website, setWebsite] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/affiliates/login', { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/affiliates/register', { name, email, password, company, website });
      toast.success('Registration submitted! You will be notified once approved.');
      setTab('login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="affiliate-login-page">
      <Card className="w-full max-w-md">
        <CardContent className="p-6 space-y-5">
          <div className="text-center">
            <img src="/logo.png" alt="Wayfinder" className="w-12 h-12 rounded-xl mx-auto mb-3" />
            <h1 className="text-xl font-bold font-['Outfit']">Affiliate Portal</h1>
            <p className="text-sm text-muted-foreground">Wayfinder Guides</p>
          </div>
          <div className="flex gap-1 bg-muted rounded-lg p-1">
            <button onClick={() => setTab('login')} className={`flex-1 py-2 text-sm font-medium rounded-md transition ${tab === 'login' ? 'bg-background shadow-sm' : ''}`} data-testid="affiliate-login-tab">Sign In</button>
            <button onClick={() => setTab('register')} className={`flex-1 py-2 text-sm font-medium rounded-md transition ${tab === 'register' ? 'bg-background shadow-sm' : ''}`} data-testid="affiliate-register-tab">Apply</button>
          </div>
          {tab === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-3">
              <div><label className="text-sm font-medium">Email</label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="affiliate-login-email" /></div>
              <div><label className="text-sm font-medium">Password</label><Input type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="affiliate-login-password" /></div>
              <Button type="submit" disabled={loading} className="w-full bg-[#1A9DA5] hover:bg-[#158D92]" data-testid="affiliate-login-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign In'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-3">
              <div><label className="text-sm font-medium">Name</label><Input value={name} onChange={e => setName(e.target.value)} required data-testid="affiliate-register-name" /></div>
              <div><label className="text-sm font-medium">Email</label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="affiliate-register-email" /></div>
              <div><label className="text-sm font-medium">Password</label><Input type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="affiliate-register-password" /></div>
              <div><label className="text-sm font-medium">Company <span className="text-muted-foreground">(optional)</span></label><Input value={company} onChange={e => setCompany(e.target.value)} /></div>
              <div><label className="text-sm font-medium">Website <span className="text-muted-foreground">(optional)</span></label><Input value={website} onChange={e => setWebsite(e.target.value)} /></div>
              <Button type="submit" disabled={loading} className="w-full bg-[#1A9DA5] hover:bg-[#158D92]" data-testid="affiliate-register-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply to Join'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [stats, setStats] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ payment_method: '', paypal_email: '', bank_name: '', bank_account_name: '', bank_account_number: '', bank_routing_number: '' });

  const authGet = (url) => api.get(url, { headers: { Authorization: `Bearer ${token}` } });
  const authPut = (url, data) => api.put(url, data, { headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    const load = async () => {
      try {
        const [meRes, statsRes, commRes, payRes] = await Promise.all([
          authGet('/affiliate/me'),
          authGet('/affiliate/stats'),
          authGet('/affiliate/commissions'),
          authGet('/affiliate/payouts'),
        ]);
        setAffiliate(meRes.data);
        setStats(statsRes.data);
        setCommissions(commRes.data);
        setPayouts(payRes.data);
        setPaymentForm({
          payment_method: meRes.data.payment_method || '',
          paypal_email: meRes.data.paypal_email || '',
          bank_name: meRes.data.bank_name || '',
          bank_account_name: meRes.data.bank_account_name || '',
          bank_account_number: meRes.data.bank_account_number || '',
          bank_routing_number: meRes.data.bank_routing_number || '',
        });
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem(TOKEN_KEY);
          onLogout();
        }
      }
      setLoading(false);
    };
    load();
  }, [token, onLogout]);

  const referralLink = affiliate ? `${window.location.origin}?ref=${affiliate.referral_code}` : '';
  const copyLink = () => { navigator.clipboard.writeText(referralLink); toast.success('Referral link copied!'); };
  const handleLogout = () => { localStorage.removeItem(TOKEN_KEY); onLogout(); };

  const savePaymentMethod = async () => {
    setSavingPayment(true);
    try {
      const { data } = await authPut('/affiliate/payment-method', paymentForm);
      setAffiliate(data);
      toast.success('Payment method saved');
    } catch { toast.error('Failed to save'); }
    setSavingPayment(false);
  };

  const commissionStatusColor = (s) => {
    if (s === 'paid') return 'bg-emerald-500/10 text-emerald-600';
    if (s === 'approved') return 'bg-blue-500/10 text-blue-600';
    return 'bg-amber-500/10 text-amber-600';
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#1A9DA5] animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-background" data-testid="affiliate-dashboard">
      <header className="border-b bg-card">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Wayfinder" className="w-6 h-6 rounded" />
            <span className="font-bold font-['Outfit']">Affiliate Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:block">{affiliate?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="affiliate-logout-btn">
              <LogOut className="w-4 h-4 mr-1" />Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Referral Link */}
        <Card className="border-[#1A9DA5]/20 bg-[#1A9DA5]/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Link2 className="w-4 h-4 text-[#1A9DA5]" />
              <span className="text-sm font-semibold">Your Referral Link</span>
              <Badge className="bg-[#1A9DA5]/10 text-[#1A9DA5] ml-auto">{stats?.commission_rate || 0}% commission</Badge>
            </div>
            <div className="flex gap-2">
              <Input readOnly value={referralLink} className="font-mono text-sm bg-background" data-testid="referral-link-input" />
              <Button onClick={copyLink} className="bg-[#1A9DA5] hover:bg-[#158D92] shrink-0" data-testid="copy-referral-link-btn">
                <Copy className="w-4 h-4 mr-1" />Copy
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="p-4 text-center"><MousePointerClick className="w-5 h-5 text-[#1A9DA5] mx-auto mb-1" /><p className="text-2xl font-bold">{stats.total_clicks}</p><p className="text-xs text-muted-foreground">Total Clicks</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><TrendingUp className="w-5 h-5 text-[#1A9DA5] mx-auto mb-1" /><p className="text-2xl font-bold">{stats.total_conversions}</p><p className="text-xs text-muted-foreground">Conversions ({stats.conversion_rate}%)</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><DollarSign className="w-5 h-5 text-emerald-500 mx-auto mb-1" /><p className="text-2xl font-bold text-emerald-600">${stats.total_earnings.toFixed(2)}</p><p className="text-xs text-muted-foreground">Total Earnings</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><Banknote className="w-5 h-5 text-emerald-600 mx-auto mb-1" /><p className="text-2xl font-bold text-emerald-600">${stats.paid_earnings.toFixed(2)}</p><p className="text-xs text-muted-foreground">Paid Out</p></CardContent></Card>
          </div>
        )}

        {/* Earnings Breakdown */}
        {stats && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold text-sm mb-3">Earnings Breakdown</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div><p className="text-lg font-bold text-amber-600">${stats.pending_earnings.toFixed(2)}</p><p className="text-xs text-muted-foreground">Pending</p></div>
                <div><p className="text-lg font-bold text-blue-600">${stats.approved_earnings.toFixed(2)}</p><p className="text-xs text-muted-foreground">Approved</p></div>
                <div><p className="text-lg font-bold text-emerald-600">${stats.paid_earnings.toFixed(2)}</p><p className="text-xs text-muted-foreground">Paid Out</p></div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          {['commissions', 'payouts', 'payment'].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${tab === t ? 'border-[#1A9DA5] text-[#1A9DA5]' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid={`aff-tab-${t}`}>
              {t === 'payment' ? 'Payment Method' : t}
            </button>
          ))}
        </div>

        {tab === 'commissions' && (
          <div className="space-y-2">
            {commissions.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p>No commissions yet. Share your referral link to start earning!</p>
              </CardContent></Card>
            ) : commissions.map(c => (
              <Card key={c.id}>
                <CardContent className="p-3 flex items-center justify-between text-sm">
                  <div><p className="font-medium">Order ${c.order_amount?.toFixed(2)}</p><p className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</p></div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-[#1A9DA5]">${c.commission_amount?.toFixed(2)}</span>
                    <Badge className={commissionStatusColor(c.status)}>{c.status}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {tab === 'payouts' && (
          <div className="space-y-2">
            {payouts.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">
                <Banknote className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p>No payouts yet. Payouts are processed by the admin once commissions are approved.</p>
              </CardContent></Card>
            ) : payouts.map(p => (
              <Card key={p.id}>
                <CardContent className="p-3 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium text-emerald-600">${p.amount?.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()} via {p.method || 'N/A'}</p>
                  </div>
                  <Badge className="bg-emerald-500/10 text-emerald-600">{p.status}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {tab === 'payment' && (
          <Card data-testid="payment-method-section">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className="w-4 h-4 text-[#1A9DA5]" />
                <h3 className="font-semibold text-sm">Payment Method</h3>
              </div>
              <p className="text-xs text-muted-foreground">Set your preferred payment method for receiving payouts.</p>
              <Select value={paymentForm.payment_method} onValueChange={v => setPaymentForm(f => ({ ...f, payment_method: v }))}>
                <SelectTrigger data-testid="payment-method-select"><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>

              {paymentForm.payment_method === 'paypal' && (
                <div>
                  <label className="text-sm font-medium">PayPal Email</label>
                  <Input value={paymentForm.paypal_email} onChange={e => setPaymentForm(f => ({ ...f, paypal_email: e.target.value }))} placeholder="your@paypal.com" data-testid="paypal-email-input" />
                </div>
              )}

              {paymentForm.payment_method === 'bank' && (
                <div className="space-y-3">
                  <div><label className="text-sm font-medium">Bank Name</label><Input value={paymentForm.bank_name} onChange={e => setPaymentForm(f => ({ ...f, bank_name: e.target.value }))} placeholder="Bank of America" data-testid="bank-name-input" /></div>
                  <div><label className="text-sm font-medium">Account Holder Name</label><Input value={paymentForm.bank_account_name} onChange={e => setPaymentForm(f => ({ ...f, bank_account_name: e.target.value }))} data-testid="bank-account-name-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-sm font-medium">Account Number</label><Input value={paymentForm.bank_account_number} onChange={e => setPaymentForm(f => ({ ...f, bank_account_number: e.target.value }))} data-testid="bank-account-input" /></div>
                    <div><label className="text-sm font-medium">Routing Number</label><Input value={paymentForm.bank_routing_number} onChange={e => setPaymentForm(f => ({ ...f, bank_routing_number: e.target.value }))} data-testid="bank-routing-input" /></div>
                  </div>
                </div>
              )}

              <Button onClick={savePaymentMethod} disabled={savingPayment || !paymentForm.payment_method} className="bg-[#1A9DA5] hover:bg-[#158D92]" data-testid="save-payment-btn">
                {savingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Save Payment Method
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

export default function AffiliateDashboard() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  if (!token) return <AffiliateLogin onLogin={(t) => setToken(t)} />;
  return <Dashboard token={token} onLogout={() => setToken(null)} />;
}
